import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useCategoryTree, type CategoryNode } from '../../hooks/useCategories';
import type { Category } from '../../types';

interface CategorySelectorProps {
  selectedCategoryId: string | null;
  onSelect: (categoryId: string) => void;
  categories: Category[];
}

interface FlatCategory {
  id: string;
  name: string;
  color: string;
  icon?: string;
  parentName: string | null;
  depth: number;
}

function flattenForSearch(
  nodes: CategoryNode[],
  parentName: string | null = null,
  depth = 0
): FlatCategory[] {
  const result: FlatCategory[] = [];
  for (const node of nodes) {
    if (node.children.length > 0) {
      // Parent category - recurse into children
      result.push(...flattenForSearch(node.children, node.name, depth + 1));
    } else {
      // Leaf category - add to list
      result.push({
        id: node.id,
        name: node.name,
        color: node.color,
        icon: node.icon,
        parentName,
        depth,
      });
    }
  }
  return result;
}

function CategoryOption({
  node,
  depth,
  selectedId,
  onSelect,
  highlightedId,
  onMouseEnter,
}: {
  node: CategoryNode;
  depth: number;
  selectedId: string | null;
  onSelect: (categoryId: string) => void;
  highlightedId: string | null;
  onMouseEnter: (categoryId: string) => void;
}) {
  const isSelected = node.id === selectedId;
  const isHighlighted = node.id === highlightedId;
  const paddingLeft = depth * 16 + 8;
  const hasChildren = node.children.length > 0;

  if (hasChildren) {
    // Parent category: render as non-clickable header
    return (
      <>
        <div
          className="w-full text-left px-2 py-2 sm:py-1 bg-gray-50 flex items-center gap-2 text-gray-600 font-medium text-sm cursor-default"
          style={{ paddingLeft: `${paddingLeft}px` }}
          role="group"
          aria-label={`${node.name} category group`}
        >
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: node.color }}
          />
          <span className="truncate">
            {node.icon && <span className="mr-1">{node.icon}</span>}
            {node.name}
          </span>
        </div>
        {node.children.map((child) => (
          <CategoryOption
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            onSelect={onSelect}
            highlightedId={highlightedId}
            onMouseEnter={onMouseEnter}
          />
        ))}
      </>
    );
  }

  // Leaf category: render as clickable option
  return (
    <button
      type="button"
      onClick={() => onSelect(node.id)}
      onMouseEnter={() => onMouseEnter(node.id)}
      className={`w-full text-left px-2 py-3 sm:py-1.5 hover:bg-gray-100 flex items-center gap-2 min-h-[44px] sm:min-h-0 ${
        isHighlighted
          ? 'bg-blue-100'
          : isSelected
            ? 'bg-blue-50 font-medium'
            : ''
      }`}
      style={{ paddingLeft: `${paddingLeft}px` }}
      role="option"
      aria-selected={isSelected}
      data-category-id={node.id}
    >
      {depth > 0 && <span className="text-gray-400">└</span>}
      <span
        className="w-3 h-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: node.color }}
      />
      <span className="truncate">
        {node.icon && <span className="mr-1">{node.icon}</span>}
        {node.name}
      </span>
      {isSelected && <span className="ml-auto text-brand-primary">✓</span>}
    </button>
  );
}

function FilteredCategoryOption({
  category,
  isSelected,
  isHighlighted,
  onSelect,
  onMouseEnter,
}: {
  category: FlatCategory;
  isSelected: boolean;
  isHighlighted: boolean;
  onSelect: (categoryId: string) => void;
  onMouseEnter: (categoryId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(category.id)}
      onMouseEnter={() => onMouseEnter(category.id)}
      className={`w-full text-left px-3 py-3 sm:py-1.5 hover:bg-gray-100 flex items-center gap-2 min-h-[44px] sm:min-h-0 ${
        isHighlighted
          ? 'bg-blue-100'
          : isSelected
            ? 'bg-blue-50 font-medium'
            : ''
      }`}
      role="option"
      aria-selected={isSelected}
      data-category-id={category.id}
    >
      <span
        className="w-3 h-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: category.color }}
      />
      <span className="truncate flex-1">
        {category.icon && <span className="mr-1">{category.icon}</span>}
        {category.name}
      </span>
      {category.parentName && (
        <span className="text-xs text-gray-400 truncate">
          {category.parentName}
        </span>
      )}
      {isSelected && <span className="ml-1 text-brand-primary">✓</span>}
    </button>
  );
}

export function CategorySelector({
  selectedCategoryId,
  onSelect,
  categories,
}: CategorySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const categoryTree = useCategoryTree();

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId),
    [categories, selectedCategoryId]
  );

  const flatCategories = useMemo(
    () => flattenForSearch(categoryTree),
    [categoryTree]
  );

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const query = searchQuery.toLowerCase();
    return flatCategories.filter(
      (cat) =>
        cat.name.toLowerCase().includes(query) ||
        (cat.parentName && cat.parentName.toLowerCase().includes(query))
    );
  }, [flatCategories, searchQuery]);

  // Reset state when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedId(null);
    }
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Set initial highlight when filtered results change
  useEffect(() => {
    if (filteredCategories && filteredCategories.length > 0) {
      setHighlightedId(filteredCategories[0].id);
    } else if (filteredCategories && filteredCategories.length === 0) {
      setHighlightedId(null);
    }
  }, [filteredCategories]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = useCallback(
    (categoryId: string) => {
      onSelect(categoryId);
      setIsOpen(false);
    },
    [onSelect]
  );

  const handleMouseEnter = useCallback((categoryId: string) => {
    setHighlightedId(categoryId);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }

      if (event.key === 'Enter' && highlightedId) {
        event.preventDefault();
        handleSelect(highlightedId);
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const list = filteredCategories ?? flatCategories;
        if (list.length === 0) return;

        const currentIndex = list.findIndex((c) => c.id === highlightedId);
        let nextIndex: number;

        if (event.key === 'ArrowDown') {
          nextIndex = currentIndex < list.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : list.length - 1;
        }

        const nextId = list[nextIndex].id;
        setHighlightedId(nextId);

        // Scroll highlighted item into view
        const element = listRef.current?.querySelector(
          `[data-category-id="${nextId}"]`
        );
        element?.scrollIntoView({ block: 'nearest' });
      }
    },
    [highlightedId, filteredCategories, flatCategories, handleSelect]
  );

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1 px-3 py-2 sm:px-2 sm:py-1 rounded-full text-sm sm:text-xs text-white cursor-pointer hover:opacity-90 transition-opacity min-h-[44px] sm:min-h-0"
        style={{
          backgroundColor: selectedCategory?.color ?? '#9E9E9E',
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedCategory?.icon && (
          <span className="mr-0.5">{selectedCategory.icon}</span>
        )}
        <span>{selectedCategory?.name ?? 'Sin categoría'}</span>
        <span className="ml-0.5">▼</span>
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          role="listbox"
          aria-label="Seleccionar categoría"
        >
          <div className="p-2 border-b border-gray-100">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Buscar categoría..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              aria-label="Buscar categoría"
            />
          </div>
          <div ref={listRef} className="max-h-56 overflow-y-auto">
            {filteredCategories ? (
              filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <FilteredCategoryOption
                    key={category.id}
                    category={category}
                    isSelected={category.id === selectedCategoryId}
                    isHighlighted={category.id === highlightedId}
                    onSelect={handleSelect}
                    onMouseEnter={handleMouseEnter}
                  />
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  No se encontraron categorías
                </div>
              )
            ) : (
              categoryTree.map((node) => (
                <CategoryOption
                  key={node.id}
                  node={node}
                  depth={0}
                  selectedId={selectedCategoryId}
                  onSelect={handleSelect}
                  highlightedId={highlightedId}
                  onMouseEnter={handleMouseEnter}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
